/**
 * interior_kits.ts — WHAT A PIECE IS FOR AND HOW PIECES GO TOGETHER (the interiors programme, slice B, 2026-10-01).
 * PURE data + small helpers. It reads the catalog and the legacy clearance functions; it copies none of their numbers.
 *
 * 1. PIECE METADATA (`pieceMeta`) for every catalog piece (`FURNITURE`, 56 since the interior items of 2026-10-01):
 *      · the drawn oriented bound — `drawnHalf` (catalog dims × the 1.5 render scale; an UPPER bound: `furnitureFit`
 *        scales a model uniformly INSIDE w × h × d) — and the MEASURED drawn size of the served model (`MEASURED_MM`,
 *        read off the registry's GLBs on 2026-10-01; the bed probe re-measures and compares);
 *      · the FRONT: every slate model's working side is its +Z at yaw 0 — the plans' convention ("0 faces +Z"), the
 *        seat's occupant faces it (`deed_home.ts`); LOOKED AT on the bed's catalog sheet (hand-back part 4);
 *      · whether it blocks walking, hugs a wall, lies under things (a rug), carries things (a table top) or may stand
 *        on a top; its function (`FURNITURE_FUNCTION`), its approach sides, its seat / lie sockets (`seatPose`,
 *        `seatReach`), its COVER (the drawn height; see-through pieces never block sight).
 * 2. KITS: named clusters with relative transforms and the anchors the cluster provides — a `counter.service` is
 *    counter segments + a staff side + customer and queue points + stools; a `bed.treatment` is a cot + a caregiver's
 *    bedside + a tray. A member hangs off an earlier member's SIDE, so a kit's geometry follows the catalog (a dim
 *    change moves the chair with its table). A member may name the WANTED piece a later kit revision swaps in
 *    (`wants`) — the stand-in works today.
 * 3. LIVED-IN CLUSTERS (report §5.4): small kits (a reading chair with its table and lamp, a plant by a window) seated
 *    last, from their own seed stream, never on a proven route.
 *
 * AUTHORING RULE: never `flush: 'back'` a member SHALLOWER than the one it hangs off — the legacy centre rule keeps every
 * centre 0.55 m off a wall, so a shallow piece flush with a deep one's back drags the whole kit off the wall.
 *
 * KIT FRAME: the kit's FRONT is +Z, its BACK −Z (the wall side of a wall kit), RIGHT +X, LEFT −X; all turns are
 * quarter turns (0 = faces the kit's front). Integer millimetres throughout (`resolveKit`).
 *
 * Bump `KIT_VERSION` on ANY change that can move, add, remove or re-key a piece: it is part of the recipe and the
 * digest. A pinned layout (slice C's `structure_layout`) is re-planned only by an explicit Refit.
 */
import { FURNITURE, type FurnitureSpec } from './furniture_catalog.js';
import { FURNITURE_FUNCTION, FURNITURE_USE_M, seatPose, seatReach, type FurnitureFunction } from './furniture_functions.js';
import { drawnHalf, footprintClear, stairEdgeX, DOOR_BOX_HALF, WALL_SINK, type PlanFrame } from './interior_plans.js';
import { CONSOLE_KEEP_W, CONSOLE_KEEP_FRONT, CONSOLE_KEEP_BACK, offTheConsole } from './furnish_packs.js';
import { consoleSeat } from './structure_access.js';
import { COVER_SIGHT_M, type AccessRole, type AnchorKind, type PieceTier } from './interior_descriptor.js';
import { KIT_PROPS, isKitProp, kitProp, propHalf } from './interior_kit_props.js';

export const KIT_VERSION = 'kits-3';

/**
 * THE KEYS THAT JOINED AT EACH KIT VERSION (slice N, 2026-10-01). `kits-2`: thirteen generated interior items took the
 * place of the stand-ins the kits named them for (`wants`) — a counter with a customer face for the bar's cupboard
 * doors, a reception desk for the console desk turned round, a notice board for the holo screen, and so on. A kit that
 * seats one of these keys was RESEATED at that version (`kitReseatVersions`); an archetype whose programs name a reseated kit
 * carries its authored revision + 1 per such version (interior_archetypes.ts derives it from this table — never a
 * hand-kept list). The fourteenth item, the wall sconce, is a shell fixture (`FIXTURE_MODEL`), seated by no kit.
 *
 * `kits-3` (round 2, the same day): the eight pieces the kits still stood stand-ins for — the bar table, the back-bar
 * and retail shelves, the retail island, the process machine, the stage curtain, the lectern and the altar — and the
 * COUNTER again: its served model was stretched to 1.08 m (it stood 0.91 m beside a 0.98 m stool), so every counter
 * kit's cover height and the lift of the teller glass standing ON it changed; a kit that seats it is reseated a
 * second time.
 */
export const KIT_VERSION_KEYS: Readonly<Record<string, readonly string[]>> = {
  'kits-2': ['counter_segment', 'reception_desk', 'service_trolley', 'notice_case', 'privacy_screen', 'seat_row', 'display_plinth',
    'exhibit_case', 'council_table', 'parts_rack', 'locker_bank', 'wash_unit', 'vault_door'],
  'kits-3': ['cantina_table', 'service_shelf', 'display_shelf', 'display_table', 'process_machine', 'stage_curtain', 'lectern', 'altar',
    'counter_segment'],
};
/** KITS RE-AUTHORED AT A VERSION WITHOUT A NEW KEY: `perform.corner` stood a rug in for the riser; at kits-3 it seats
 *  slice F's walk-on `stage_riser` — a prop the stage kits have seated since kits-1, so the key table cannot say it. */
export const KIT_VERSION_KITS: Readonly<Record<string, readonly string[]>> = {
  'kits-3': ['perform.corner'],
};

/** A SHELL FIXTURE that has a generated model: the fixture's key (interior_kit_props.ts `FIXTURES` — where
 *  `shellFixtures` seats it) → the catalog piece drawn there instead of its boxes. The model is fitted like any piece
 *  (`furnitureFit` on its catalog entry) and seated with its BACK (−Z) on the fixture's back plane, its base at the
 *  fixture's `y`. A drawer with no such model loaded draws the boxes — the stand-in stands today. */
export const FIXTURE_MODEL: Readonly<Record<string, string>> = { wall_sconce: 'wall_sconce' };

// ─── Piece metadata ──────────────────────────────────────────────────────────────────────────────────────────────────

/** The served models' DRAWN size [w, h, d] in mm at yaw 0 (`furnitureFit` on the registry's LOD1 GLBs, 2026-10-01). */
/** NOTE (the bed's re-measure, 2026-10-01): `furnitureFit` clamps a model's box to ≥ 0.05 units per axis before it
 *  fits — the rug's cooked thickness is under that, so its fit is 0.04 / 0.05 × 1.5 = 1.2 and it draws 2.27 × 1.04 m,
 *  not the 3.0 × 1.37 an unclamped measure gives. The table below is the drawn size as the GAME fits it. */
export const MEASURED_MM: Readonly<Record<string, readonly [number, number, number]>> = {
  armchair_wing: [1256, 1426, 1350], bed_double: [1876, 1350, 1940], bed_single: [1449, 1050, 2787],
  bench_hall: [2522, 750, 699], bench_work: [1849, 1637, 1050], bookcase_arch: [920, 1516, 525],
  cabinet_low: [1525, 659, 675], chair_dining: [709, 1189, 825], chest_storage: [1174, 825, 742],
  cooler_unit: [1200, 2384, 953], counter_bar: [2097, 1500, 910], crate_storage: [1859, 1161, 1500],
  desk_terminal: [1465, 1042, 1200], desk_writing: [1537, 1170, 833], fabricator_pod: [993, 1106, 1050],
  guestbook_pedestal: [1011, 1724, 900], hover_chair: [1203, 1206, 1500], kitchen_unit: [1800, 1650, 804],
  lamp_floor: [750, 1948, 674], lamp_table: [419, 750, 419], light_column: [524, 2059, 525],
  mirror_tall: [751, 2008, 750], orchestrion: [1004, 1798, 900], plant_large: [1007, 1051, 1050],
  plant_small: [442, 450, 442], planter_hydro: [2694, 1602, 1200], pod_lounge: [1650, 1918, 1489],
  pod_sleep: [1800, 1347, 1722], rug_woven: [2273, 39, 1040], safe_vault: [1211, 1271, 1350],
  screen_holo: [776, 1365, 600], shelf_unit: [989, 1331, 525], sofa_low: [2602, 1200, 1174],
  statue_small: [450, 540, 340], stool_shop: [621, 975, 654], stove_hearth: [954, 1288, 1050],
  table_dining: [2700, 1126, 1353], table_low: [1483, 460, 1050], table_side: [750, 819, 750],
  terrarium_pod: [750, 928, 617], wardrobe_tall: [1581, 2588, 900], weapon_rack: [974, 2153, 750],
  // the interior items (2026-10-01): furnitureFit on the served LOD1s (tools/asset-pipeline/out-interior-items/measured-mm.mjs)
  counter_segment: [2400, 1078, 579], reception_desk: [2100, 964, 1004], service_trolley: [750, 783, 435],
  notice_case: [1500, 1800, 599], privacy_screen: [1688, 1903, 390], seat_row: [2290, 1271, 750],
  display_plinth: [900, 773, 899], exhibit_case: [1874, 1612, 750], council_table: [2700, 740, 1293],
  parts_rack: [1800, 1541, 651], locker_bank: [1408, 2100, 532], wash_unit: [1650, 1205, 962],
  vault_door: [2400, 2391, 1100], wall_sconce: [170, 510, 105],
  // round 2 (kits-3): the same measure on the eight new pieces; the counter's 1078 above is its stretched model
  cantina_table: [752, 1080, 751], service_shelf: [1203, 2201, 621], display_shelf: [1500, 2024, 619],
  display_table: [2100, 887, 791], process_machine: [1091, 2000, 1499], stage_curtain: [1198, 2700, 259],
  lectern: [612, 1301, 632], altar: [1500, 1126, 1600],
};

/** Pieces that lie UNDER others and never block (a rug). */
const UNDERLAY: ReadonlySet<string> = new Set(['rug_woven']);
/** Pieces small enough to stand ON a top. */
const ON_TOP: ReadonlySet<string> = new Set(['lamp_table', 'plant_small', 'statue_small', 'terrarium_pod']);
/** Tops that carry things: the height is the served model's drawn height. */
const SURFACES: ReadonlySet<string> = new Set(['table_side', 'table_low', 'cabinet_low', 'desk_writing', 'chest_storage',
  'counter_segment', 'council_table', 'display_plinth', 'exhibit_case', 'cantina_table', 'altar']);
/** A top that is NOT the model's own top, as a fraction of its drawn height: the exhibit case carries its exhibits on
 *  the glass SHELF inside it (measured on the served model: the shelf's upper face at 57.5 % of the case's height). */
const SURFACE_FRAC: Readonly<Record<string, number>> = {
  exhibit_case: 0.575,
  // the altar is a standing stone on a low DAIS: what it carries (the two lamps) stands on the dais, before the stone —
  // its upper face at 17.8 % of the model's height (measured on the served model: 0.20 m of 1.13)
  altar: 0.178,
};
/** Pieces whose back belongs on a wall. */
const WALL: ReadonlySet<string> = new Set([
  'wardrobe_tall', 'shelf_unit', 'cabinet_low', 'bookcase_arch', 'kitchen_unit', 'cooler_unit', 'bench_work', 'weapon_rack',
  'mirror_tall', 'stove_hearth', 'orchestrion', 'fabricator_pod', 'planter_hydro', 'safe_vault', 'crate_storage', 'chest_storage',
  'bed_single', 'bed_double', 'pod_sleep', 'bench_hall', 'sofa_low', 'desk_writing',
  'locker_bank', 'parts_rack', 'wash_unit', 'vault_door', 'notice_case',
  'service_shelf', 'display_shelf', 'process_machine', 'stage_curtain', 'altar',
]);
/** Pieces a sight line passes: a holo, a lamp post, foliage, an open rack. */
const SEE_THROUGH: ReadonlySet<string> = new Set(['screen_holo', 'lamp_floor', 'light_column', 'plant_large', 'plant_small', 'weapon_rack', 'terrarium_pod', 'lamp_table', 'parts_rack', 'wall_sconce']);
/** Seat / lie sockets across a piece's width (piece-local metres); everything else has one at its centre. */
const SOCKET_X: Readonly<Record<string, readonly number[]>> = { sofa_low: [-0.65, 0.65], bench_hall: [-0.8, 0, 0.8], bed_double: [-0.45, 0.45], seat_row: [-0.76, 0, 0.76] };

export type Side = 'front' | 'back' | 'left' | 'right';

export interface PieceMeta {
  key: string; spec: FurnitureSpec;
  /** Every slate model's working side is +Z at yaw 0. */
  front: 'z+';
  blocksWalk: boolean; wall: boolean; underlay: boolean; onTop: boolean;
  /** Top height a supported piece stands at (m), or null. */
  surface: number | null;
  use: FurnitureFunction | null;
  /** Where a user stands, in preference order. */
  approach: readonly Side[];
  /** How near the centre the standing point must be (m): `seatReach` for a seat / bed, `FURNITURE_USE_M` otherwise. */
  reach: number;
  sockets: ReadonlyArray<{ dx: number; pose: 'sit' | 'lie'; eye: number }>;
  /** Drawn half extents at yaw 0 (m) — the bound every check uses (`drawnHalf`). */
  half: { w: number; d: number };
  /** The served model's drawn size (m), or the bound when it was never measured. */
  drawn: { w: number; h: number; d: number };
  /** COVER: drawn height (m) and whether it stops a sight line. */
  coverHeight: number; blocksSight: boolean;
}

const metaCache = new Map<string, PieceMeta>();
let metaStamp = '';
const stampOf = (): string => Object.values(FURNITURE).map((s) => `${s.key}:${s.w}:${s.h}:${s.d}`).join('|');

/** Drop the metadata and kit caches when the catalog changed under us (a harness does this on purpose; a wanted item
 *  landing does it for real). `planInterior` and `validateDescriptor` call it ONCE per plan — never per piece: the
 *  stamp is a string over the whole catalog, and building it on every `pieceMeta` was most of a small room's plan time. */
export function syncCatalog(): void {
  const st = stampOf();
  if (st !== metaStamp) { metaCache.clear(); kitCache.clear(); metaStamp = st; }
}

/** A PROCEDURAL piece (interior_kit_props.ts) answers like a catalog piece: it draws at exactly its size, has no
 *  function and no sockets; a `walkOn` riser or a floor marking is free ground (the planner's "underlay"). */
function propMeta(key: string): PieceMeta | null {
  const p = kitProp(key);
  if (!p) return null;
  const hit = metaCache.get(key);
  if (hit) return hit;
  const meta: PieceMeta = {
    key, spec: { key, name: p.name, cat: 'decor', w: p.w / 1.5, h: p.h / 1.5, d: p.d / 1.5, desc: p.desc },
    front: 'z+',
    blocksWalk: p.blocksWalk, wall: !!p.wall, underlay: !p.blocksWalk, onTop: false,
    surface: p.surface ? p.h : null,
    use: null, approach: ['front', 'left', 'right'], reach: FURNITURE_USE_M, sockets: [],
    half: { w: p.w / 2, d: p.d / 2 },
    drawn: { w: p.w, h: p.h, d: p.d },
    coverHeight: p.h, blocksSight: p.blocksWalk && p.h >= COVER_SIGHT_M && !p.seeThrough,
  };
  metaCache.set(key, meta);
  return meta;
}

export function pieceMeta(key: string): PieceMeta | null {
  const spec = FURNITURE[key];
  if (!spec) return propMeta(key);
  if (metaStamp === '') syncCatalog();
  const hit = metaCache.get(key);
  if (hit) return hit;
  const h0 = drawnHalf(key, 0);
  const bound = { w: h0.hx * 2, h: (spec.h * h0.hx * 2) / spec.w, d: h0.hz * 2 };
  const m = MEASURED_MM[key];
  // A measured size never exceeds the bound it was fitted into (a changed catalog falls back to the bound).
  const drawn = m && m[0] / 1000 <= bound.w + 0.002 && m[2] / 1000 <= bound.d + 0.002 ? { w: m[0] / 1000, h: m[1] / 1000, d: m[2] / 1000 } : bound;
  const use = FURNITURE_FUNCTION[key] ?? null;
  const underlay = UNDERLAY.has(key), onTop = ON_TOP.has(key);
  const pose: 'sit' | 'lie' | null = use === 'sit' ? 'sit' : use === 'lie' ? 'lie' : null;
  const sockets = pose ? (SOCKET_X[key] ?? [0]).map((dx) => ({ dx, pose, eye: seatPose(pose, spec.h).eye })) : [];
  const meta: PieceMeta = {
    key, spec, front: 'z+',
    blocksWalk: !underlay, wall: WALL.has(key), underlay, onTop,
    surface: SURFACES.has(key) ? drawn.h * (SURFACE_FRAC[key] ?? 1) : null,
    use,
    approach: use === 'lie' ? ['left', 'right', 'front'] : use === 'sit' ? ['front', 'left', 'right', 'back'] : ['front', 'left', 'right'],
    reach: pose ? seatReach(key) : FURNITURE_USE_M,
    sockets,
    half: { w: h0.hx, d: h0.hz },
    drawn,
    coverHeight: drawn.h,
    blocksSight: !underlay && drawn.h >= COVER_SIGHT_M && !SEE_THROUGH.has(key),
  };
  metaCache.set(key, meta);
  return meta;
}

/** Drawn AABB half extents in integer mm at a quarter turn (0..3), rounded UP — the planner's box. */
export function halfMm(key: string, quarter: number): { hx: number; hz: number } {
  const h = boundHalf(key, 0);
  const w = Math.ceil(h.hx * 1000 - 1e-6), d = Math.ceil(h.hz * 1000 - 1e-6);
  return (quarter & 1) === 0 ? { hx: w, hz: d } : { hx: d, hz: w };
}

// ─── The legacy clearance rules for ANY piece (a catalog piece asks the legacy function; a prop asks its box) ─────────

/** A piece's drawn half extents on the room axes: the catalog's `drawnHalf`, or a procedural prop's own size. */
export function boundHalf(key: string, yaw: number): { hx: number; hz: number } {
  return isKitProp(key) ? propHalf(key, yaw) : drawnHalf(key, yaw);
}

/** `footprintClear`'s three rules for a box of EXPLICIT half extents — the walls past the sink, the doorway's first
 *  3.4 m, the stair column with its rail. The legacy function takes a catalog key (its box is `drawnHalf(key)`); a
 *  prop has no catalog entry, so its box is given. A MIRROR of interior_plans.ts `footprintClear`, pinned by
 *  verify:interior-planner over every catalog piece (`boxFootprintClear(drawnHalf(key)) ≡ footprintClear(key)`). */
export function boxFootprintClear(f: PlanFrame, hx: number, hz: number, x: number, z: number, underlay: boolean): boolean {
  if (Math.abs(x) + hx > f.hw + WALL_SINK + 1e-9 || Math.abs(z) + hz > f.hd + WALL_SINK + 1e-9) return false;
  if (!underlay && x - hx < DOOR_BOX_HALF && x + hx > -DOOR_BOX_HALF && z - hz < -f.hd + 3.4) return false;
  const z0 = f.stair.z - f.stair.d / 2 - 1.2, z1 = f.stair.z + f.stair.d / 2 + 0.9;
  if (x - hx < stairEdgeX(f) && z + hz > z0 && z - hz < z1) return false;
  return true;
}
/** Law 38 for any piece: the legacy function for a catalog key (asked, never copied), the box rule for a prop — a
 *  prop never lies in the doorway (only the woven rug has that exemption). */
export function pieceFootprintClear(f: PlanFrame, key: string, x: number, z: number, yaw: number): boolean {
  if (!isKitProp(key)) return footprintClear(f, key, x, z, yaw);
  const h = propHalf(key, yaw);
  return boxFootprintClear(f, h.hx, h.hz, x, z, false);
}
/** THE CONSOLE'S GROUND for any piece: `offTheConsole` keeps a catalog slot or drops it (asked, never copied). A prop
 *  must pass that SAME call (so a pack view of a planned floor loses nothing to it) AND its real box must clear the
 *  console's keep — `offTheConsole` reads an unknown key as a 1 × 1 m box, which a 3 m riser is not. The box test is
 *  the function's own formula with the prop's half extents (pinned by the harness over every catalog piece). */
export function boxOffConsole(f: PlanFrame, hx: number, hz: number, x: number, z: number): boolean {
  const c = consoleSeat({ hw: f.hw, hd: f.hd, stair: { x: f.stair.x, z: f.stair.z, w: f.stair.w ?? 1.6, d: f.stair.d } });
  const meetX = Math.abs(x - c.x) <= CONSOLE_KEEP_W + hx;
  const meetZ = z + hz >= c.z - CONSOLE_KEEP_FRONT && z - hz <= c.z + CONSOLE_KEEP_BACK;
  return !(meetX && meetZ);
}
export function pieceOffConsole(f: PlanFrame, key: string, x: number, z: number, yaw: number): boolean {
  if (offTheConsole([{ key, x, z, yaw }], f).length === 0) return false;
  if (!isKitProp(key)) return true;
  const h = propHalf(key, yaw);
  return boxOffConsole(f, h.hx, h.hz, x, z);
}

// ─── Kits ────────────────────────────────────────────────────────────────────────────────────────────────────────────

export interface KitMember {
  /** Unique within the kit; the slot id is `<zone>/<kit>/<role>#n`. */
  role: string;
  /** The catalog piece seated TODAY. */
  key: string;
  /** The WANTED piece a later kit revision swaps in (hand-back "WANTED ITEMS"); `key` is its stand-in. */
  wants?: string;
  /** Where: the first member is the kit origin; the rest hang off an earlier member's side, `gap` m edge to edge,
   *  shifted `along` m (to the kit's right for front / back, to its front for left / right) or `flush` with an edge. */
  at?: { of: string; side: Side; gap: number; along?: number; flush?: 'back' | 'front' | 'left' | 'right' };
  /** Stands ON that member's top (a support relation): offset from its centre (m). */
  on?: { of: string; dx?: number; dz?: number };
  /** Lies UNDER that member (an underlay), centred on it + offset. */
  under?: { of: string; dx?: number; dz?: number };
  /** Quarter turns from the kit's front (0 faces +Z). `face: 'toward'` turns it to face the member it hangs off. */
  turn?: 0 | 1 | 2 | 3;
  face?: 'toward';
  tier: Exclude<PieceTier, 'owned'>;
  /** May be left out when it does not fit (the kit stays valid without it). */
  optional?: boolean;
  /** Roles that must be seated for this one to make sense (a chair needs its table) — beyond `at.of` / `on.of`. */
  needs?: string[];
}

export interface KitAnchor {
  kind: AnchorKind;
  /** The member used. */
  of: string;
  /** Preferred standing side of that member and the distance from its edge (m). Without `reserve`, the planner may
   *  take another free side inside the piece's reach. */
  side: Side; dist?: number; along?: number;
  /** The standing point is RESERVED when the kit is seated (a service point, a bedside): kept clear for good. */
  reserve?: boolean;
  /** Half extents of the use envelope, kit-local (hx across, hz along the side's normal). */
  envelope?: { hx: number; hz: number };
  /** A seat / lie socket on the member (index into its sockets), with the pose. */
  socket?: number;
  /** The user looks AWAY from the member (a performer faces the room). */
  faceOut?: boolean;
  access?: AccessRole;
  /** Without it the kit is not worth seating (a counter nobody can stand behind). */
  required?: boolean;
}

export interface KitSpec {
  id: string;
  label: string;
  /** `wall` = its back goes on a wall / an edge; `free` = stands in the room. */
  mount: 'wall' | 'free';
  members: KitMember[];
  anchors: KitAnchor[];
  /** A lived-in cluster (report §5.4): seated last, from the detail stream, never on a route. */
  cluster?: boolean;
}

const seat = (of: string, socket = 0): KitAnchor => ({ kind: 'seat', of, side: 'front', socket });
const store = (of: string, access?: AccessRole): KitAnchor => ({ kind: 'storage', of, side: 'front', dist: 0.45, access });

/** Counter segments in a row: s0 at the origin, the rest alternately to its right and left. The segment's CUSTOMER
 *  face (panels, foot rail, the light strip) is its +Z — the kit's front; staff stand behind it (kits-2). */
function counterRow(n: number): KitMember[] {
  const out: KitMember[] = [{ role: 's0', key: 'counter_segment', tier: 'primary' }];
  for (let i = 1; i < n; i++) {
    const right = i % 2 === 1;
    const of = i <= 2 ? 's0' : `s${i - 2}`;
    out.push({ role: `s${i}`, key: 'counter_segment', at: { of, side: right ? 'right' : 'left', gap: 0.02, flush: 'back' }, tier: 'primary', optional: i >= 2 });
  }
  return out;
}

const KIT_LIST: KitSpec[] = [
  // ── sleeping ──
  { id: 'bed.single', label: 'single bed', mount: 'wall',
    members: [
      { role: 'bed', key: 'bed_single', tier: 'primary' },
      { role: 'stand', key: 'table_side', at: { of: 'bed', side: 'right', gap: 0.05, along: -0.8 }, tier: 'secondary', optional: true },
      { role: 'lamp', key: 'lamp_table', on: { of: 'stand' }, tier: 'detail', optional: true },
    ],
    anchors: [{ kind: 'bed', of: 'bed', side: 'left', dist: 0.45, socket: 0, required: true }] },
  { id: 'bed.double', label: 'double bed', mount: 'wall',
    members: [
      { role: 'bed', key: 'bed_double', tier: 'primary' },
      { role: 'stand', key: 'table_side', at: { of: 'bed', side: 'right', gap: 0.05, along: -0.8 }, tier: 'secondary', optional: true },
      { role: 'lamp', key: 'lamp_table', on: { of: 'stand' }, tier: 'detail', optional: true },
    ],
    anchors: [{ kind: 'bed', of: 'bed', side: 'left', dist: 0.45, socket: 0, required: true }, { kind: 'bed', of: 'bed', side: 'right', dist: 0.45, socket: 1 }] },
  { id: 'bed.treatment', label: 'treatment cot with a bedside', mount: 'wall',
    members: [
      { role: 'cot', key: 'bed_single', tier: 'primary' },
      { role: 'tray', key: 'service_trolley', at: { of: 'cot', side: 'right', gap: 0.05, along: -0.8 }, tier: 'secondary', optional: true },
    ],
    anchors: [
      // the caregiver's space: a real standing box on the cot's open long side, kept clear for good
      { kind: 'bedside', of: 'cot', side: 'left', dist: 0.5, reserve: true, envelope: { hx: 0.7, hz: 0.45 }, access: 'employee', required: true },
      { kind: 'bed', of: 'cot', side: 'front', dist: 0.45, socket: 0, required: true },
    ] },
  // ── storage ──
  { id: 'store.wardrobe', label: 'wardrobe', mount: 'wall', members: [{ role: 'wardrobe', key: 'wardrobe_tall', tier: 'primary' }], anchors: [store('wardrobe')] },
  { id: 'store.chest', label: 'chest', mount: 'wall', members: [{ role: 'chest', key: 'chest_storage', tier: 'primary' }], anchors: [store('chest')] },
  { id: 'store.cabinet', label: 'low cabinet', mount: 'wall', members: [{ role: 'cabinet', key: 'cabinet_low', tier: 'primary' }], anchors: [store('cabinet')] },
  { id: 'store.bookcase', label: 'bookcase', mount: 'wall', members: [{ role: 'bookcase', key: 'bookcase_arch', tier: 'primary' }], anchors: [store('bookcase')] },
  { id: 'store.shelf', label: 'shelf', mount: 'wall', members: [{ role: 'shelf', key: 'shelf_unit', tier: 'primary' }], anchors: [store('shelf')] },
  // ── cooking ──
  { id: 'kitchen.galley', label: 'galley kitchen', mount: 'wall',
    members: [
      { role: 'kitchen', key: 'kitchen_unit', tier: 'primary' },
      { role: 'cooler', key: 'cooler_unit', at: { of: 'kitchen', side: 'right', gap: 0.03, flush: 'back' }, tier: 'secondary', optional: true },
    ],
    anchors: [{ kind: 'work', of: 'kitchen', side: 'front', dist: 0.45, required: true }, store('kitchen'), store('cooler')] },
  // ── tables ──
  { id: 'table.dining', label: 'dining table for four', mount: 'free',
    members: [
      { role: 'table', key: 'table_dining', tier: 'primary' },
      { role: 'chair0', key: 'chair_dining', at: { of: 'table', side: 'front', gap: 0.03, along: -0.6 }, face: 'toward', tier: 'primary' },
      { role: 'chair1', key: 'chair_dining', at: { of: 'table', side: 'back', gap: 0.03, along: 0.6 }, face: 'toward', tier: 'primary', optional: true },
      { role: 'chair2', key: 'chair_dining', at: { of: 'table', side: 'front', gap: 0.03, along: 0.6 }, face: 'toward', tier: 'secondary', optional: true },
      { role: 'chair3', key: 'chair_dining', at: { of: 'table', side: 'back', gap: 0.03, along: -0.6 }, face: 'toward', tier: 'secondary', optional: true },
    ],
    anchors: [seat('chair0'), seat('chair1'), seat('chair2'), seat('chair3')] },
  { id: 'table.pair', label: 'table for two', mount: 'free',
    members: [
      { role: 'table', key: 'table_dining', tier: 'primary' },
      { role: 'chair0', key: 'chair_dining', at: { of: 'table', side: 'front', gap: 0.03 }, face: 'toward', tier: 'primary' },
      { role: 'chair1', key: 'chair_dining', at: { of: 'table', side: 'back', gap: 0.03 }, face: 'toward', tier: 'primary', optional: true },
    ],
    anchors: [seat('chair0'), seat('chair1')] },
  { id: 'table.stools', label: 'small table and stools', mount: 'free',
    members: [
      // kits-3: the chest-high bar table (1.08 m — the stools' seats are under its top)
      { role: 'table', key: 'cantina_table', tier: 'primary' },
      { role: 'stool0', key: 'stool_shop', at: { of: 'table', side: 'front', gap: 0.03 }, face: 'toward', tier: 'primary' },
      { role: 'stool1', key: 'stool_shop', at: { of: 'table', side: 'back', gap: 0.03 }, face: 'toward', tier: 'primary', optional: true },
      { role: 'stool2', key: 'stool_shop', at: { of: 'table', side: 'left', gap: 0.03 }, face: 'toward', tier: 'secondary', optional: true },
    ],
    anchors: [seat('stool0'), seat('stool1'), seat('stool2')] },
  // The Frosthearth's bay: a table end-on to the wall between two benches — six seats facing each other.
  { id: 'booth.bay', label: 'sheltered bay', mount: 'wall',
    members: [
      { role: 'table', key: 'table_dining', turn: 1, tier: 'primary' },
      { role: 'bench0', key: 'bench_hall', at: { of: 'table', side: 'left', gap: 0.03 }, face: 'toward', tier: 'primary' },
      { role: 'bench1', key: 'bench_hall', at: { of: 'table', side: 'right', gap: 0.03 }, face: 'toward', tier: 'primary', optional: true },
    ],
    anchors: [seat('bench0', 0), seat('bench0', 1), seat('bench0', 2), seat('bench1', 0), seat('bench1', 1), seat('bench1', 2)] },
  // ── sitting ──
  { id: 'lounge.set', label: 'sofa and low table', mount: 'wall',
    members: [
      { role: 'sofa', key: 'sofa_low', tier: 'primary' },
      { role: 'table', key: 'table_low', at: { of: 'sofa', side: 'front', gap: 0.3 }, tier: 'primary', optional: true },
      { role: 'chair', key: 'armchair_wing', at: { of: 'table', side: 'right', gap: 0.3 }, face: 'toward', tier: 'secondary', optional: true },
      { role: 'rug', key: 'rug_woven', under: { of: 'table' }, tier: 'secondary', optional: true },
    ],
    anchors: [seat('sofa', 0), seat('sofa', 1), seat('chair')] },
  { id: 'seat.reading', label: 'reading chair, side table and lamp', mount: 'wall', cluster: true,
    members: [
      { role: 'chair', key: 'armchair_wing', tier: 'primary' },
      { role: 'stand', key: 'table_side', at: { of: 'chair', side: 'right', gap: 0.05 }, tier: 'secondary', optional: true },
      { role: 'lamp', key: 'lamp_table', on: { of: 'stand' }, tier: 'detail', optional: true },
    ],
    anchors: [seat('chair')] },
  { id: 'seat.chair', label: 'a chair and a side table', mount: 'wall',
    members: [
      { role: 'chair', key: 'chair_dining', tier: 'primary' },
      { role: 'stand', key: 'table_side', at: { of: 'chair', side: 'right', gap: 0.05 }, tier: 'secondary', optional: true },
      { role: 'lamp', key: 'lamp_table', on: { of: 'stand' }, tier: 'detail', optional: true },
    ],
    anchors: [seat('chair')] },
  { id: 'seat.waiting', label: 'waiting bench', mount: 'wall',
    members: [{ role: 'bench', key: 'bench_hall', tier: 'primary' }],
    anchors: [seat('bench', 0), seat('bench', 1), seat('bench', 2)] },
  { id: 'seat.stools', label: 'two stools', mount: 'free',
    members: [
      { role: 'stool0', key: 'stool_shop', tier: 'primary' },
      { role: 'stool1', key: 'stool_shop', at: { of: 'stool0', side: 'right', gap: 0.45 }, tier: 'primary', optional: true },
    ],
    anchors: [seat('stool0'), seat('stool1')] },
  // ── service ──
  ...[2, 3, 4].map((n): KitSpec => ({
    id: `counter.service.${n}`, label: `service counter, ${n} segments`, mount: 'free',
    members: [
      ...counterRow(n),
      // the bar stools stand at the LAST segment — the order point (s0) stays open
      { role: 'stool0', key: 'stool_shop', at: { of: `s${n - 1}`, side: 'front', gap: 0.05, along: -0.5 }, face: 'toward', tier: 'secondary', optional: true },
      { role: 'stool1', key: 'stool_shop', at: { of: `s${n - 1}`, side: 'front', gap: 0.05, along: 0.5 }, face: 'toward', tier: 'secondary', optional: true },
    ],
    anchors: [
      { kind: 'service-staff', of: 's0', side: 'back', dist: 0.5, reserve: true, envelope: { hx: 0.5, hz: 0.4 }, access: 'employee', required: true },
      { kind: 'service-customer', of: 's0', side: 'front', dist: 0.5, reserve: true, envelope: { hx: 0.45, hz: 0.4 }, required: true },
      { kind: 'queue', of: 's0', side: 'front', dist: 1.4, reserve: true, envelope: { hx: 0.4, hz: 0.4 } },
      { kind: 'queue', of: 's0', side: 'front', dist: 2.3, reserve: true, envelope: { hx: 0.4, hz: 0.4 } },
      seat('stool0'), seat('stool1'),
    ],
  })),
  { id: 'counter.checkout', label: 'checkout counter', mount: 'free',
    members: [{ role: 'counter', key: 'counter_segment', tier: 'primary' }],
    anchors: [
      { kind: 'service-staff', of: 'counter', side: 'back', dist: 0.5, reserve: true, envelope: { hx: 0.5, hz: 0.4 }, access: 'employee', required: true },
      { kind: 'service-customer', of: 'counter', side: 'front', dist: 0.5, reserve: true, envelope: { hx: 0.45, hz: 0.4 }, required: true },
      { kind: 'queue', of: 'counter', side: 'front', dist: 1.4, reserve: true, envelope: { hx: 0.4, hz: 0.4 } },
    ] },
  { id: 'prep.backbar', label: 'back bar: galley, cooler, shelf', mount: 'wall',
    members: [
      { role: 'kitchen', key: 'kitchen_unit', tier: 'primary' },
      { role: 'cooler', key: 'cooler_unit', at: { of: 'kitchen', side: 'right', gap: 0.03, flush: 'back' }, tier: 'primary', optional: true },
      // kits-3: the back-bar shelf — bottles toward the room (its +Z), 2.2 m of it; not flush (it is shallower than the galley)
      { role: 'shelf', key: 'service_shelf', at: { of: 'kitchen', side: 'left', gap: 0.03 }, tier: 'secondary', optional: true },
    ],
    anchors: [{ kind: 'work', of: 'kitchen', side: 'front', dist: 0.45, access: 'employee', required: true }, store('kitchen', 'employee'), store('cooler', 'employee'), store('shelf', 'employee')] },
  { id: 'desk.reception', label: 'reception desk', mount: 'free',
    members: [
      // the desk's visitor ledge and status light are its +Z — the kit's front; the clerk's well faces the chair (kits-2:
      // the console desk that stood here was turned half round so its screen faced the clerk)
      { role: 'desk', key: 'reception_desk', tier: 'primary' },
      { role: 'chair', key: 'chair_dining', at: { of: 'desk', side: 'back', gap: 0.05 }, face: 'toward', tier: 'primary' },
    ],
    anchors: [
      // the clerk SITS: the staff anchor is the chair's socket
      { kind: 'service-staff', of: 'chair', side: 'left', socket: 0, access: 'employee', required: true },
      { kind: 'service-customer', of: 'desk', side: 'front', dist: 0.5, reserve: true, envelope: { hx: 0.45, hz: 0.4 }, required: true },
      { kind: 'queue', of: 'desk', side: 'front', dist: 1.4, reserve: true, envelope: { hx: 0.4, hz: 0.4 } },
    ] },
  // ── working ──
  { id: 'desk.study', label: 'writing desk and chair', mount: 'wall',
    members: [
      { role: 'desk', key: 'desk_writing', tier: 'primary' },
      { role: 'chair', key: 'chair_dining', at: { of: 'desk', side: 'front', gap: 0.03 }, face: 'toward', tier: 'primary', optional: true },
    ],
    anchors: [{ kind: 'work', of: 'chair', side: 'left', socket: 0 }] },
  // ── performance ──
  { id: 'perform.corner', label: 'performance corner', mount: 'wall',
    members: [
      { role: 'organ', key: 'orchestrion', tier: 'primary' },
      // kits-3: slice F's walk-on riser before the organ (it took a rug's place): 3.0 × 2.1 m, its back edge 5 cm off
      // the organ's front, so the performer's ground below IS its top. It never blocks a walk (`walkOn`: a 0.3 m floor).
      { role: 'riser', key: 'stage_riser', under: { of: 'organ', dz: 1.55 }, tier: 'secondary', optional: true },
      { role: 'light', key: 'light_column', at: { of: 'organ', side: 'right', gap: 0.25 }, tier: 'detail', optional: true },
    ],
    anchors: [
      // kits-3: the performer's reserved ground is the riser's WHOLE TOP (`onRiser`'s rule — 0.1 m inside its rim). An
      // underlay is seated without asking what stands there and never keeps a later piece off, so with the rug's small
      // envelope (0.7 × 0.55) a table and its stools were seated half-buried in the riser (LOOKED AT: the Radiant Cup).
      { kind: 'perform', of: 'organ', side: 'front', dist: 1.1, reserve: true, envelope: { hx: 1.4, hz: 0.95 }, faceOut: true, access: 'employee', required: true },
      { kind: 'stand', of: 'organ', side: 'right', dist: 0.5 },
    ] },
  // ── display ──
  { id: 'display.wall', label: 'wall display shelf', mount: 'wall',
    members: [{ role: 'shelf', key: 'display_shelf', tier: 'primary' }], // kits-3: stocked shelves under a teal header
    anchors: [{ kind: 'display', of: 'shelf', side: 'front', dist: 0.5 }] },
  { id: 'display.island', label: 'display table', mount: 'free',
    members: [
      // kits-3: the retail island. Its goods are MODELLED on it (top and lower shelf), so the jade bird and the
      // terrarium that dressed the cabinet's top are gone — a second layer of goods would stand inside the first.
      { role: 'base', key: 'display_table', tier: 'primary' },
    ],
    anchors: [{ kind: 'display', of: 'base', side: 'front', dist: 0.5 }, { kind: 'display', of: 'base', side: 'back', dist: 0.5 }] },
  { id: 'display.hero', label: 'hero pedestal', mount: 'free',
    members: [
      { role: 'plinth', key: 'display_plinth', tier: 'primary' },
      { role: 'item', key: 'terrarium_pod', on: { of: 'plinth' }, tier: 'primary', optional: true },
    ],
    anchors: [{ kind: 'display', of: 'plinth', side: 'front', dist: 0.5 }] },
  { id: 'sign.screen', label: 'notice board', mount: 'wall',
    members: [{ role: 'screen', key: 'notice_case', tier: 'primary' }],
    anchors: [{ kind: 'display', of: 'screen', side: 'front', dist: 0.6 }] },
  { id: 'stock.back', label: 'back stock', mount: 'wall',
    members: [
      { role: 'shelf0', key: 'shelf_unit', tier: 'primary' },
      { role: 'shelf1', key: 'shelf_unit', at: { of: 'shelf0', side: 'right', gap: 0.05, flush: 'back' }, tier: 'secondary', optional: true },
      { role: 'crate', key: 'crate_storage', at: { of: 'shelf0', side: 'left', gap: 0.1, flush: 'back' }, tier: 'secondary', optional: true },
    ],
    anchors: [store('shelf0', 'employee'), store('shelf1', 'employee'), store('crate', 'employee')] },
  { id: 'supply.wall', label: 'supplies: wash unit and cooler', mount: 'wall',
    members: [
      { role: 'cabinet', key: 'wash_unit', tier: 'primary' },
      { role: 'cooler', key: 'cooler_unit', at: { of: 'cabinet', side: 'right', gap: 0.05, flush: 'back' }, tier: 'primary', optional: true },
    ],
    anchors: [store('cabinet', 'employee'), store('cooler', 'employee')] },
  // ── lived-in clusters and single details (seated last, from the detail stream, never on a route) ──
  { id: 'detail.plant', label: 'broadleaf planter', mount: 'wall', cluster: true, members: [{ role: 'plant', key: 'plant_large', tier: 'detail' }], anchors: [] },
  { id: 'detail.lamp', label: 'floor lamp', mount: 'wall', cluster: true, members: [{ role: 'lamp', key: 'lamp_floor', tier: 'detail' }], anchors: [{ kind: 'stand', of: 'lamp', side: 'front', dist: 0.45 }] },
  { id: 'detail.column', label: 'light column', mount: 'wall', cluster: true, members: [{ role: 'light', key: 'light_column', tier: 'detail' }], anchors: [] },
  { id: 'detail.hearth', label: 'hearth stove', mount: 'wall', cluster: true, members: [{ role: 'hearth', key: 'stove_hearth', tier: 'detail' }], anchors: [{ kind: 'stand', of: 'hearth', side: 'front', dist: 0.5 }] },
  { id: 'detail.guestbook', label: 'guest book', mount: 'wall', cluster: true, members: [{ role: 'book', key: 'guestbook_pedestal', tier: 'detail' }], anchors: [{ kind: 'stand', of: 'book', side: 'front', dist: 0.45 }] },
  { id: 'detail.mirror', label: 'tall mirror', mount: 'wall', cluster: true, members: [{ role: 'mirror', key: 'mirror_tall', tier: 'detail' }], anchors: [{ kind: 'stand', of: 'mirror', side: 'front', dist: 0.5 }] },
  { id: 'detail.sidecase', label: 'low cabinet with a terrarium', mount: 'wall', cluster: true,
    members: [{ role: 'cabinet', key: 'cabinet_low', tier: 'detail' }, { role: 'item', key: 'terrarium_pod', on: { of: 'cabinet' }, tier: 'detail', optional: true }],
    anchors: [store('cabinet')] },
  { id: 'detail.rug', label: 'woven rug', mount: 'free', cluster: true, members: [{ role: 'rug', key: 'rug_woven', tier: 'detail' }], anchors: [] },
];

// ─── THE BREADTH KITS (slice F, 2026-10-01) ─────────────────────────────────────────────────────────────────────────
// Appended, never interleaved: every wave-1 kit above keeps its id, its members and its place. A member whose key is a
// PROCEDURAL prop (interior_kit_props.ts) stands today; `wants` names the generated model that would replace it.

/** A counter line: n segments (each with its own panelled customer face — kits-2; the procedural `counter_front`
 *  that stood before the slate's bar is gone), optionally a teller's glass on each and a pair of queue rails either
 *  side of the order point. */
function frontedCounter(id: string, label: string, n: number, o: { glass: boolean; rails: boolean }): KitSpec {
  const members: KitMember[] = counterRow(n);
  for (let i = 0; i < n; i++) {
    if (o.glass) members.push({ role: `glass${i}`, key: 'teller_screen', on: { of: `s${i}` }, tier: 'secondary', optional: true });
  }
  if (o.rails) {
    members.push({ role: 'rail0', key: 'barrier_rail', at: { of: 's0', side: 'front', gap: 1.05, along: -1.0 }, turn: 1, tier: 'secondary', optional: true });
    members.push({ role: 'rail1', key: 'barrier_rail', at: { of: 's0', side: 'front', gap: 1.05, along: 1.0 }, turn: 1, tier: 'secondary', optional: true });
  }
  const anchors: KitAnchor[] = [
    { kind: 'service-staff', of: 's0', side: 'back', dist: 0.5, reserve: true, envelope: { hx: 0.5, hz: 0.4 }, access: 'employee', required: true },
    // 0.6: clear of the foot rail on the segment's customer face
    { kind: 'service-customer', of: 's0', side: 'front', dist: 0.6, reserve: true, envelope: { hx: 0.45, hz: 0.4 }, required: true },
    { kind: 'queue', of: 's0', side: 'front', dist: 1.5, reserve: true, envelope: { hx: 0.4, hz: 0.4 } },
    { kind: 'queue', of: 's0', side: 'front', dist: 2.4, reserve: true, envelope: { hx: 0.4, hz: 0.4 } },
  ];
  for (let i = 1; i < n; i++) {
    anchors.push({ kind: 'service-staff', of: `s${i}`, side: 'back', dist: 0.5, reserve: true, envelope: { hx: 0.5, hz: 0.4 }, access: 'employee' });
    anchors.push({ kind: 'service-customer', of: `s${i}`, side: 'front', dist: 0.6, reserve: true, envelope: { hx: 0.45, hz: 0.4 } });
  }
  return { id, label, mount: 'free', members, anchors };
}

/** A performer's / speaker's / officiant's place ON a riser: the whole top is the reserved envelope, so nothing else
 *  is ever seated half-buried in it. 0.1 m before the centre, so `faceOut` always turns to the kit's front. */
// the performer's ground is the riser's top, kept clear for good; it stops 0.25 m short of the wall behind (a reserved
// envelope may not touch a room wall) and reaches the riser's front edge
const onRiser = (of: string): KitAnchor => ({ kind: 'perform', of, side: 'front', dist: -0.9, reserve: true, envelope: { hx: 1.4, hz: 0.8 }, faceOut: true, access: 'employee', required: true });

const BREADTH_KITS: KitSpec[] = [
  // ── service ──
  frontedCounter('counter.teller.3', 'teller line, 3 positions', 3, { glass: true, rails: true }),
  frontedCounter('counter.teller.2', 'teller line, 2 positions', 2, { glass: true, rails: true }),
  frontedCounter('counter.ticket', 'ticket counter', 2, { glass: false, rails: true }),
  frontedCounter('counter.kiosk', 'kiosk counter', 1, { glass: false, rails: false }),
  { id: 'queue.rail', label: 'queue rail', mount: 'free',
    members: [
      { role: 'rail0', key: 'barrier_rail', tier: 'primary' },
      { role: 'rail1', key: 'barrier_rail', at: { of: 'rail0', side: 'right', gap: 0.9 }, tier: 'primary', optional: true },
    ],
    anchors: [] },
  // the Hive's casework is the SHELL's (building_interiors.ts §2.9: a 0.7 m counter 2.1 m before the back wall): the
  // staff mat lies 1.2 m off the wall behind it, the customer stands 0.6 m before its front face — 1.45 m beyond the
  // mat's front edge, the queue a pace behind
  { id: 'till.hive', label: "staff mat behind the shell's counter", mount: 'free',
    members: [{ role: 'mat', key: 'service_mat', tier: 'primary' }],
    anchors: [
      { kind: 'service-staff', of: 'mat', side: 'front', dist: -0.35, reserve: true, envelope: { hx: 0.5, hz: 0.35 }, access: 'employee', required: true },
      { kind: 'service-customer', of: 'mat', side: 'front', dist: 1.45, reserve: true, envelope: { hx: 0.45, hz: 0.4 }, required: true },
      { kind: 'queue', of: 'mat', side: 'front', dist: 2.35, reserve: true, envelope: { hx: 0.4, hz: 0.4 } },
    ] },
  { id: 'vault.front', label: 'vault door and strongboxes', mount: 'wall',
    members: [
      { role: 'door', key: 'vault_door', tier: 'primary' },
      { role: 'safe0', key: 'safe_vault', at: { of: 'door', side: 'left', gap: 0.2, flush: 'back' }, tier: 'primary', optional: true },
      { role: 'safe1', key: 'safe_vault', at: { of: 'door', side: 'right', gap: 0.2, flush: 'back' }, tier: 'secondary', optional: true },
    ],
    anchors: [
      // the guard's post before the door — kept clear for good
      { kind: 'stand', of: 'door', side: 'front', dist: 0.9, reserve: true, envelope: { hx: 0.6, hz: 0.5 }, faceOut: true, access: 'guarded', required: true },
      store('safe0', 'guarded'), store('safe1', 'guarded'),
    ] },
  { id: 'store.safes', label: 'strongboxes', mount: 'wall',
    members: [
      { role: 'safe0', key: 'safe_vault', tier: 'primary' },
      { role: 'safe1', key: 'safe_vault', at: { of: 'safe0', side: 'right', gap: 0.1, flush: 'back' }, tier: 'secondary', optional: true },
    ],
    anchors: [store('safe0', 'guarded'), store('safe1', 'guarded')] },
  // ── seating ──
  { id: 'seat.row', label: 'a row of seats', mount: 'free',
    members: [{ role: 'bench', key: 'seat_row', tier: 'primary' }],
    anchors: [seat('bench', 0), seat('bench', 1), seat('bench', 2)] },
  { id: 'seat.pew', label: 'a bench with a kneeler', mount: 'free',
    members: [
      { role: 'bench', key: 'bench_hall', tier: 'primary' },
      { role: 'pad', key: 'kneeler_pad', at: { of: 'bench', side: 'front', gap: 0.3 }, tier: 'secondary', optional: true },
    ],
    anchors: [seat('bench', 0), seat('bench', 1), seat('bench', 2)] },
  { id: 'lounge.pods', label: 'lounge pods and a low table', mount: 'wall',
    members: [
      { role: 'pod0', key: 'pod_lounge', tier: 'primary' },
      { role: 'table', key: 'table_low', at: { of: 'pod0', side: 'right', gap: 0.25 }, tier: 'primary', optional: true },
      { role: 'pod1', key: 'pod_lounge', at: { of: 'table', side: 'right', gap: 0.25 }, tier: 'secondary', optional: true },
    ],
    anchors: [seat('pod0'), seat('pod1')] },
  // a free-standing conversation group: two sofas face each other across a low table on a rug
  { id: 'lounge.island', label: 'facing sofas on a rug', mount: 'free',
    members: [
      { role: 'table', key: 'table_low', tier: 'primary' },
      { role: 'sofa0', key: 'sofa_low', at: { of: 'table', side: 'back', gap: 0.35 }, face: 'toward', tier: 'primary' },
      { role: 'sofa1', key: 'sofa_low', at: { of: 'table', side: 'front', gap: 0.35 }, face: 'toward', tier: 'primary', optional: true },
      { role: 'chair', key: 'armchair_wing', at: { of: 'table', side: 'right', gap: 0.35 }, face: 'toward', tier: 'secondary', optional: true },
      { role: 'rug', key: 'rug_woven', under: { of: 'table' }, tier: 'secondary', optional: true },
    ],
    anchors: [seat('sofa0', 0), seat('sofa0', 1), seat('sofa1', 0), seat('sofa1', 1), seat('chair')] },
  // ── tables ──
  { id: 'table.council', label: 'council table', mount: 'free',
    members: [
      { role: 't0', key: 'council_table', tier: 'primary' },
      { role: 'a0', key: 'chair_dining', at: { of: 't0', side: 'front', gap: 0.03, along: -0.7 }, face: 'toward', tier: 'primary' },
      { role: 'b0', key: 'chair_dining', at: { of: 't0', side: 'back', gap: 0.03, along: 0.7 }, face: 'toward', tier: 'primary', optional: true },
      { role: 'a1', key: 'chair_dining', at: { of: 't0', side: 'front', gap: 0.03, along: 0.7 }, face: 'toward', tier: 'primary', optional: true },
      { role: 'b1', key: 'chair_dining', at: { of: 't0', side: 'back', gap: 0.03, along: -0.7 }, face: 'toward', tier: 'primary', optional: true },
      { role: 't1', key: 'council_table', at: { of: 't0', side: 'right', gap: 0 }, tier: 'primary', optional: true },
      { role: 'c0', key: 'chair_dining', at: { of: 't1', side: 'front', gap: 0.03, along: -0.7 }, face: 'toward', tier: 'secondary', optional: true },
      { role: 'd0', key: 'chair_dining', at: { of: 't1', side: 'back', gap: 0.03, along: 0.7 }, face: 'toward', tier: 'secondary', optional: true },
      { role: 'c1', key: 'chair_dining', at: { of: 't1', side: 'front', gap: 0.03, along: 0.7 }, face: 'toward', tier: 'secondary', optional: true },
      { role: 'd1', key: 'chair_dining', at: { of: 't1', side: 'back', gap: 0.03, along: -0.7 }, face: 'toward', tier: 'secondary', optional: true },
      { role: 'head0', key: 'chair_dining', at: { of: 't0', side: 'left', gap: 0.03 }, face: 'toward', tier: 'secondary', optional: true },
      { role: 'head1', key: 'chair_dining', at: { of: 't1', side: 'right', gap: 0.03 }, face: 'toward', tier: 'secondary', optional: true },
    ],
    anchors: ['a0', 'b0', 'a1', 'b1', 'c0', 'd0', 'c1', 'd1', 'head0', 'head1'].map((r) => seat(r)) },
  { id: 'table.briefing', label: 'briefing table with a holo map', mount: 'free',
    members: [
      // the map is the table's own inset (kits-2: the holo screen that stood beside the dining table is gone)
      { role: 'table', key: 'council_table', tier: 'primary' },
    ],
    anchors: [
      { kind: 'stand', of: 'table', side: 'front', dist: 0.5, along: -0.7, required: true }, { kind: 'stand', of: 'table', side: 'front', dist: 0.5, along: 0.7 },
      { kind: 'stand', of: 'table', side: 'back', dist: 0.5, along: -0.7 }, { kind: 'stand', of: 'table', side: 'back', dist: 0.5, along: 0.7 },
      { kind: 'work', of: 'table', side: 'right', dist: 0.5, access: 'employee' },
    ] },
  // the guild's feast table: benches on both long sides, six seats facing each other
  { id: 'table.long', label: 'long table and benches', mount: 'free',
    members: [
      { role: 'table', key: 'table_dining', tier: 'primary' },
      { role: 'bench0', key: 'bench_hall', at: { of: 'table', side: 'front', gap: 0.03 }, face: 'toward', tier: 'primary' },
      { role: 'bench1', key: 'bench_hall', at: { of: 'table', side: 'back', gap: 0.03 }, face: 'toward', tier: 'primary', optional: true },
    ],
    anchors: [seat('bench0', 0), seat('bench0', 1), seat('bench0', 2), seat('bench1', 0), seat('bench1', 1), seat('bench1', 2)] },
  // ── working ──
  // two console desks back to back, a clerk each side — the unit an office floor repeats
  { id: 'desk.pod', label: 'desk island for two', mount: 'free',
    members: [
      { role: 'deskA', key: 'desk_terminal', turn: 2, tier: 'primary' },
      { role: 'chairA', key: 'chair_dining', at: { of: 'deskA', side: 'back', gap: 0.05 }, face: 'toward', tier: 'primary' },
      { role: 'deskB', key: 'desk_terminal', at: { of: 'deskA', side: 'front', gap: 0.02 }, tier: 'primary', optional: true },
      { role: 'chairB', key: 'chair_dining', at: { of: 'deskB', side: 'front', gap: 0.05 }, face: 'toward', tier: 'primary', optional: true },
    ],
    anchors: [{ kind: 'work', of: 'chairA', side: 'left', socket: 0, required: true }, { kind: 'work', of: 'chairB', side: 'left', socket: 0 }] },
  { id: 'work.bench', label: 'workbench and trolley', mount: 'wall',
    members: [
      { role: 'bench', key: 'bench_work', tier: 'primary' },
      { role: 'trolley', key: 'service_trolley', at: { of: 'bench', side: 'right', gap: 0.1 }, tier: 'secondary', optional: true },
    ],
    anchors: [{ kind: 'work', of: 'bench', side: 'front', dist: 0.45, access: 'employee', required: true }, store('trolley', 'employee')] },
  { id: 'work.fab', label: 'fabricator and its output crate', mount: 'wall',
    members: [
      { role: 'fab', key: 'fabricator_pod', tier: 'primary' },
      { role: 'crate', key: 'crate_storage', at: { of: 'fab', side: 'right', gap: 0.12, flush: 'back' }, tier: 'secondary', optional: true },
    ],
    anchors: [{ kind: 'work', of: 'fab', side: 'front', dist: 0.45, access: 'employee', required: true }, store('crate', 'employee')] },
  // THE PROCESS LINE, in the order the material moves: inputs → process → inspection → outputs, a guard rail before
  // the machines. Members drop from the END when the wall is short (near first, whole first).
  { id: 'work.line', label: 'process line: rack, machines, inspection, crate', mount: 'wall',
    members: [
      { role: 'rack', key: 'parts_rack', tier: 'primary' },
      // kits-3: the process machine — control face, amber window and output tray on its +Z; 1.5 m deep (the tray), 2.0 m tall
      { role: 'fab0', key: 'process_machine', at: { of: 'rack', side: 'right', gap: 0.15, flush: 'back' }, tier: 'primary' },
      { role: 'fab1', key: 'process_machine', at: { of: 'fab0', side: 'right', gap: 0.1, flush: 'back' }, tier: 'primary', optional: true },
      { role: 'bench', key: 'bench_work', at: { of: 'fab1', side: 'right', gap: 0.15, flush: 'back' }, tier: 'primary', optional: true },
      { role: 'crate', key: 'crate_storage', at: { of: 'bench', side: 'right', gap: 0.15, flush: 'back' }, tier: 'secondary', optional: true },
      { role: 'rail', key: 'barrier_rail', at: { of: 'fab0', side: 'front', gap: 1.1, along: 0.7 }, tier: 'secondary', optional: true },
    ],
    anchors: [
      { kind: 'work', of: 'fab0', side: 'front', dist: 0.45, access: 'employee', required: true },
      { kind: 'work', of: 'fab1', side: 'front', dist: 0.45, access: 'employee' },
      { kind: 'work', of: 'bench', side: 'front', dist: 0.45, access: 'employee' },
      store('rack', 'employee'), store('crate', 'employee'),
    ] },
  { id: 'rack.parts', label: 'parts racks', mount: 'wall',
    members: [
      { role: 'r0', key: 'parts_rack', tier: 'primary' },
      { role: 'r1', key: 'parts_rack', at: { of: 'r0', side: 'right', gap: 0.05, flush: 'back' }, tier: 'secondary', optional: true },
    ],
    anchors: [store('r0', 'employee'), store('r1', 'employee')] },
  { id: 'stock.crates', label: 'crates', mount: 'wall',
    members: [
      { role: 'c0', key: 'crate_storage', tier: 'primary' },
      { role: 'c1', key: 'crate_storage', at: { of: 'c0', side: 'right', gap: 0.12, flush: 'back' }, tier: 'secondary', optional: true },
    ],
    anchors: [store('c0', 'employee'), store('c1', 'employee')] },
  { id: 'store.lockers', label: 'staff lockers and a changing bench', mount: 'wall',
    members: [
      { role: 'l0', key: 'locker_bank', tier: 'primary' },
      { role: 'l1', key: 'locker_bank', at: { of: 'l0', side: 'right', gap: 0.05, flush: 'back' }, tier: 'secondary', optional: true },
      { role: 'bench', key: 'bench_hall', at: { of: 'l0', side: 'front', gap: 1.1, along: 0.8 }, turn: 2, tier: 'secondary', optional: true },
    ],
    anchors: [store('l0', 'employee'), store('l1', 'employee'), seat('bench', 1)] },
  { id: 'store.weapons', label: 'weapon racks', mount: 'wall',
    members: [
      { role: 'w0', key: 'weapon_rack', tier: 'primary' },
      { role: 'w1', key: 'weapon_rack', at: { of: 'w0', side: 'right', gap: 0.1, flush: 'back' }, tier: 'secondary', optional: true },
    ],
    anchors: [store('w0', 'guarded'), store('w1', 'guarded')] },
  { id: 'bed.bunk', label: 'bunk and footlocker', mount: 'wall',
    members: [
      { role: 'bed', key: 'bed_single', tier: 'primary' },
      { role: 'locker', key: 'chest_storage', at: { of: 'bed', side: 'right', gap: 0.1, along: -1.0 }, tier: 'secondary', optional: true },
    ],
    anchors: [{ kind: 'bed', of: 'bed', side: 'left', dist: 0.45, socket: 0, required: true }, store('locker')] },
  { id: 'service.wash', label: 'wash and supply unit', mount: 'wall',
    members: [{ role: 'unit', key: 'wash_unit', tier: 'primary' }],
    anchors: [{ kind: 'work', of: 'unit', side: 'front', dist: 0.45, access: 'employee' }, store('unit', 'employee')] },
  // the salon's work triangle: the client sits facing the mirror, the stylist stands behind the chair
  { id: 'salon.station', label: 'mirror, chair and tray', mount: 'wall',
    members: [
      { role: 'mirror', key: 'mirror_tall', tier: 'primary' },
      { role: 'chair', key: 'hover_chair', at: { of: 'mirror', side: 'front', gap: 0.4 }, face: 'toward', tier: 'primary' },
      { role: 'tray', key: 'service_trolley', at: { of: 'chair', side: 'right', gap: 0.15 }, tier: 'secondary', optional: true },
    ],
    anchors: [
      seat('chair'),
      { kind: 'service-staff', of: 'chair', side: 'front', dist: 0.5, reserve: true, envelope: { hx: 0.45, hz: 0.35 }, access: 'employee', required: true },
      { kind: 'stand', of: 'mirror', side: 'left', dist: 0.5 },
    ] },
  // ── the garage ──
  // the marking IS the bay: its whole plate is a reserved envelope — a ride's footprint, kept clear for good
  { id: 'bay.vehicle', label: 'vehicle bay', mount: 'free',
    members: [{ role: 'mark', key: 'bay_marking', tier: 'primary' }],
    anchors: [
      { kind: 'work', of: 'mark', side: 'front', dist: -2.7, reserve: true, envelope: { hx: 1.5, hz: 2.7 }, access: 'employee', required: true },
      { kind: 'stand', of: 'mark', side: 'right', dist: 0.6 },
    ] },
  // two benches back to back in the open floor: a worker either side, a trolley at the end
  { id: 'work.island', label: 'bench island', mount: 'free',
    members: [
      { role: 'b0', key: 'bench_work', tier: 'primary' },
      { role: 'b1', key: 'bench_work', at: { of: 'b0', side: 'back', gap: 0.05 }, turn: 2, tier: 'primary', optional: true },
      { role: 'trolley', key: 'service_trolley', at: { of: 'b0', side: 'right', gap: 0.2 }, tier: 'secondary', optional: true },
    ],
    anchors: [
      { kind: 'work', of: 'b0', side: 'front', dist: 0.5, reserve: true, envelope: { hx: 0.6, hz: 0.4 }, access: 'employee', required: true },
      { kind: 'work', of: 'b1', side: 'front', dist: 0.5, reserve: true, envelope: { hx: 0.6, hz: 0.4 }, access: 'employee' },
    ] },
  // a gate is a gap between two rails with a post kept clear behind it: the checker stands there, the line forms before it
  { id: 'gate.boarding', label: 'boarding gate', mount: 'wall',
    members: [
      { role: 'rail0', key: 'barrier_rail', tier: 'primary' },
      { role: 'rail1', key: 'barrier_rail', at: { of: 'rail0', side: 'right', gap: 1.3 }, tier: 'primary', optional: true },
      { role: 'board', key: 'notice_case', at: { of: 'rail0', side: 'left', gap: 0.2 }, tier: 'secondary', optional: true },
    ],
    anchors: [
      { kind: 'stand', of: 'rail0', side: 'right', dist: 0.65, reserve: true, envelope: { hx: 0.4, hz: 0.6 }, faceOut: true, access: 'guarded', required: true },
      { kind: 'queue', of: 'rail0', side: 'front', dist: 0.9, along: 1.55, reserve: true, envelope: { hx: 0.4, hz: 0.4 } },
    ] },
  // the guarded threshold: a rail to stand behind and the sentry's own square of floor
  { id: 'guard.post', label: 'guard post', mount: 'wall',
    members: [{ role: 'rail', key: 'barrier_rail', tier: 'primary' }],
    anchors: [
      { kind: 'stand', of: 'rail', side: 'front', dist: 0.6, reserve: true, envelope: { hx: 0.6, hz: 0.5 }, faceOut: true, access: 'guarded', required: true },
    ] },
  // ── focus, stage, exhibits ──
  // kits-3: the generated stage curtain is a RIGHT-hand drape — swept folds on its +Z, gathered to a tie-back on its
  // +X. The right drape stands as modelled; the LEFT one stands half-turned (`turn: 2`), which puts its tie-back on the
  // outer side too and shows the house the drape's plainer back (no mirrored model exists: a negative scale flips winding).
  { id: 'perform.stage', label: 'stage: riser between drapes', mount: 'wall',
    members: [
      { role: 'riser', key: 'stage_riser', tier: 'primary' },
      { role: 'drape0', key: 'stage_curtain', at: { of: 'riser', side: 'left', gap: 0.05, along: -0.35 }, turn: 2, tier: 'secondary', optional: true },
      { role: 'drape1', key: 'stage_curtain', at: { of: 'riser', side: 'right', gap: 0.05, along: -0.35 }, tier: 'secondary', optional: true },
    ],
    anchors: [onRiser('riser'), { kind: 'stand', of: 'riser', side: 'front', dist: 0.7 }] },
  // a big house earns a wide stage: two risers end to end between the drapes
  { id: 'perform.stage.wide', label: 'wide stage between drapes', mount: 'wall',
    members: [
      { role: 'riser', key: 'stage_riser', tier: 'primary' },
      { role: 'riser1', key: 'stage_riser', at: { of: 'riser', side: 'right', gap: 0 }, tier: 'primary' },
      { role: 'drape0', key: 'stage_curtain', at: { of: 'riser', side: 'left', gap: 0.05, along: -0.35 }, turn: 2, tier: 'secondary', optional: true },
      { role: 'drape1', key: 'stage_curtain', at: { of: 'riser1', side: 'right', gap: 0.05, along: -0.35 }, tier: 'secondary', optional: true },
    ],
    anchors: [onRiser('riser'), { ...onRiser('riser1'), required: false }, { kind: 'stand', of: 'riser', side: 'front', dist: 0.7 }] },
  { id: 'focus.dais', label: 'speaker\'s dais', mount: 'wall',
    members: [
      { role: 'riser', key: 'stage_riser', tier: 'primary' },
      // kits-3: the lectern (its emblem faces the house; the speaker stands at the riser's middle, beside it)
      { role: 'lectern', key: 'lectern', on: { of: 'riser', dx: 0.85, dz: 0.3 }, tier: 'secondary', optional: true },
      { role: 'col0', key: 'light_column', at: { of: 'riser', side: 'left', gap: 0.15, along: -0.3 }, tier: 'secondary', optional: true },
      { role: 'col1', key: 'light_column', at: { of: 'riser', side: 'right', gap: 0.15, along: -0.3 }, tier: 'secondary', optional: true },
    ],
    anchors: [onRiser('riser'), { kind: 'stand', of: 'riser', side: 'front', dist: 0.7 }] },
  // faith is quiet and communal: an offering table with two lamps, light either side, a kneeler — no idol, no god
  { id: 'focus.offering', label: 'offering table and lamps', mount: 'wall',
    members: [
      // kits-3: the altar — a standing stone with a lit niche on a low dais (it names no god). The two lamps stand ON
      // THE DAIS (`SURFACE_FRAC`), either side of the niche and before the stone, which stands at the dais's back.
      { role: 'table', key: 'altar', tier: 'primary' },
      { role: 'candle0', key: 'lamp_table', on: { of: 'table', dx: -0.4, dz: 0.28 }, tier: 'primary', optional: true },
      { role: 'candle1', key: 'lamp_table', on: { of: 'table', dx: 0.4, dz: 0.28 }, tier: 'primary', optional: true },
      { role: 'col0', key: 'light_column', at: { of: 'table', side: 'left', gap: 0.35 }, tier: 'secondary', optional: true },
      { role: 'col1', key: 'light_column', at: { of: 'table', side: 'right', gap: 0.35 }, tier: 'secondary', optional: true },
      { role: 'pad', key: 'kneeler_pad', at: { of: 'table', side: 'front', gap: 1.15 }, tier: 'secondary', optional: true },
    ],
    anchors: [
      { kind: 'perform', of: 'table', side: 'front', dist: 0.5, reserve: true, envelope: { hx: 0.5, hz: 0.4 }, faceOut: true, access: 'employee', required: true },
      { kind: 'stand', of: 'table', side: 'front', dist: 1.9 },
    ] },
  // kits-2: the vitrine. Its exhibits stand ON ITS SHELF (`SURFACE_FRAC`), not on its head; its back is a blank panel,
  // so an island is two cases back to back — a front, and a place to look from, on both sides.
  { id: 'exhibit.case', label: 'exhibit case island', mount: 'free',
    members: [
      { role: 'base', key: 'exhibit_case', tier: 'primary' },
      { role: 'item', key: 'statue_small', on: { of: 'base', dx: -0.45 }, tier: 'primary', optional: true },
      { role: 'item2', key: 'plant_small', on: { of: 'base', dx: 0.45 }, tier: 'secondary', optional: true },
      { role: 'base2', key: 'exhibit_case', at: { of: 'base', side: 'back', gap: 0.02 }, turn: 2, tier: 'primary', optional: true },
      { role: 'item3', key: 'statue_small', on: { of: 'base2', dx: -0.45 }, tier: 'secondary', optional: true },
    ],
    anchors: [{ kind: 'display', of: 'base', side: 'front', dist: 0.5, required: true }, { kind: 'display', of: 'base2', side: 'back', dist: 0.5 }] },
  { id: 'exhibit.plinth', label: 'exhibit on a plinth', mount: 'free',
    members: [
      { role: 'plinth', key: 'display_plinth', tier: 'primary' },
      { role: 'item', key: 'statue_small', on: { of: 'plinth' }, tier: 'primary', optional: true },
    ],
    anchors: [{ kind: 'display', of: 'plinth', side: 'front', dist: 0.5, required: true }] },
  // a wall exhibit kept back by a rail: the visitor reads it from the rail
  { id: 'exhibit.wall', label: 'wall exhibit behind a rail', mount: 'wall',
    members: [
      { role: 'case', key: 'exhibit_case', tier: 'primary' },
      // kits-2: the generated vitrine is EMPTY (the bookcase that stood here carried its own books) — LOOKED AT on the
      // bed: a wall of empty cases is not a museum. Two exhibits stand on its shelf (`SURFACE_FRAC`), as in the island.
      { role: 'item', key: 'statue_small', on: { of: 'case', dx: -0.45 }, tier: 'primary', optional: true },
      { role: 'item2', key: 'plant_small', on: { of: 'case', dx: 0.45 }, tier: 'secondary', optional: true },
      { role: 'panel', key: 'notice_case', at: { of: 'case', side: 'right', gap: 0.2 }, tier: 'secondary', optional: true },
      { role: 'rail', key: 'barrier_rail', at: { of: 'case', side: 'front', gap: 0.75 }, tier: 'secondary', optional: true },
    ],
    anchors: [{ kind: 'display', of: 'case', side: 'front', dist: 1.5, required: true }] },
  // ── the garden room ──
  { id: 'garden.planter', label: 'planter trough', mount: 'wall',
    members: [{ role: 'planter', key: 'planter_hydro', tier: 'primary' }],
    anchors: [{ kind: 'work', of: 'planter', side: 'front', dist: 0.45 }] },
  { id: 'garden.green', label: 'planters in a group', mount: 'wall', cluster: true,
    members: [
      { role: 'plant0', key: 'plant_large', tier: 'detail' },
      { role: 'plant1', key: 'plant_large', at: { of: 'plant0', side: 'right', gap: 0.15, along: 0.25 }, tier: 'detail', optional: true },
    ],
    anchors: [] },
  // ── SCREENS: a room divided without being sealed (the route proof keeps every anchor reachable round them) ──
  { id: 'screen.divider', label: 'two screens with a way through', mount: 'free',
    members: [
      { role: 'p0', key: 'privacy_screen', tier: 'primary' },
      { role: 'p1', key: 'privacy_screen', at: { of: 'p0', side: 'right', gap: 1.3 }, tier: 'primary', optional: true },
    ],
    anchors: [] },
  { id: 'screen.single', label: 'a screen', mount: 'free',
    members: [{ role: 'p0', key: 'privacy_screen', tier: 'primary' }],
    anchors: [] },
  // the bed behind a screen: a sleeping CORNER in a big single room (the bedside stays open; the screen stands beyond it)
  { id: 'bed.double.screen', label: 'double bed behind a screen', mount: 'wall',
    members: [
      { role: 'bed', key: 'bed_double', tier: 'primary' },
      { role: 'stand', key: 'table_side', at: { of: 'bed', side: 'right', gap: 0.05, along: -0.8 }, tier: 'secondary', optional: true },
      { role: 'lamp', key: 'lamp_table', on: { of: 'stand' }, tier: 'detail', optional: true },
      { role: 'screen', key: 'privacy_screen', at: { of: 'bed', side: 'left', gap: 1.25, along: 0.45 }, turn: 1, tier: 'secondary', optional: true },
      { role: 'foot', key: 'privacy_screen', at: { of: 'bed', side: 'front', gap: 1.0, along: 0.5 }, tier: 'secondary', optional: true },
    ],
    anchors: [{ kind: 'bed', of: 'bed', side: 'left', dist: 0.45, socket: 0, required: true }, { kind: 'bed', of: 'bed', side: 'right', dist: 0.45, socket: 1 }] },
  // a cot, its tray, the caregiver's bedside — and a screen between this bay and the next
  { id: 'bed.treatment.screen', label: 'screened treatment bay', mount: 'wall',
    members: [
      { role: 'cot', key: 'bed_single', tier: 'primary' },
      { role: 'tray', key: 'service_trolley', at: { of: 'cot', side: 'right', gap: 0.05, along: -0.8 }, tier: 'secondary', optional: true },
      { role: 'screen', key: 'privacy_screen', at: { of: 'cot', side: 'left', gap: 1.5, along: 0.3 }, turn: 1, tier: 'secondary', optional: true },
    ],
    anchors: [
      { kind: 'bedside', of: 'cot', side: 'left', dist: 0.5, reserve: true, envelope: { hx: 0.7, hz: 0.45 }, access: 'employee', required: true },
      { kind: 'bed', of: 'cot', side: 'front', dist: 0.45, socket: 0, required: true },
    ] },
];

const ALL_KITS: KitSpec[] = [...KIT_LIST, ...BREADTH_KITS];
export const KITS: Readonly<Record<string, KitSpec>> = Object.fromEntries(ALL_KITS.map((k) => [k.id, k]));
export const KIT_IDS: readonly string[] = ALL_KITS.map((k) => k.id);
/** The kits wave 1 shipped (their geometry is pinned by every wave-1 digest). */
export const WAVE1_KIT_IDS: readonly string[] = KIT_LIST.map((k) => k.id);

// ─── Resolution: a kit's geometry in integer mm, kit-local ───────────────────────────────────────────────────────────

export interface ResolvedMember {
  role: string; key: string; wants?: string;
  /** Centre, kit-local mm (x right, z front); quarter turns from the kit's front. */
  dx: number; dz: number; turn: number;
  /** AABB half extents at that turn (mm). */
  hx: number; hz: number;
  tier: Exclude<PieceTier, 'owned'>; optional: boolean;
  /** Blocks walking (not an underlay, not standing on a top). */
  blocksWalk: boolean;
  /** Lift above the slab (mm) and the role it stands on. */
  lift: number; on?: string;
  /** Roles this one depends on (its `at.of`, `on.of`, `under.of`, and `needs`). */
  needs: string[];
}
export interface ResolvedAnchor {
  spec: KitAnchor; index: number;
  /** The member's centre and the preferred standing point, kit-local mm. */
  px: number; pz: number; ax: number; az: number;
  /** Envelope half extents (mm), kit-local. */
  ehx: number; ehz: number;
}
export interface ResolvedKit {
  spec: KitSpec;
  members: ResolvedMember[];
  anchors: ResolvedAnchor[];
  /** The box of the blocking members (mm, kit-local). */
  box: { x0: number; z0: number; x1: number; z1: number };
}

const kitCache = new Map<string, ResolvedKit>();

/** A side's outward unit normal, kit-local. */
export function sideNormal(side: Side): { nx: number; nz: number } {
  return side === 'front' ? { nx: 0, nz: 1 } : side === 'back' ? { nx: 0, nz: -1 } : side === 'right' ? { nx: 1, nz: 0 } : { nx: -1, nz: 0 };
}
const M = (v: number): number => Math.round(v * 1000);

export function resolveKit(id: string): ResolvedKit | null {
  const spec = KITS[id];
  if (!spec) return null;
  if (metaStamp === '') syncCatalog();
  const hit = kitCache.get(id);
  if (hit) return hit;
  const members: ResolvedMember[] = [];
  const by = (role: string): ResolvedMember | undefined => members.find((m) => m.role === role);
  for (const m of spec.members) {
    const meta = pieceMeta(m.key);
    if (!meta) continue; // an unknown key drops (and so does anything that hangs off it)
    const anchorRole = m.at?.of ?? m.on?.of ?? m.under?.of;
    const parent = anchorRole ? by(anchorRole) : undefined;
    if (anchorRole && !parent) continue;
    let turn: number = m.turn ?? 0;
    if (m.face === 'toward' && m.at) turn = m.at.side === 'front' ? 2 : m.at.side === 'back' ? 0 : m.at.side === 'right' ? 3 : 1;
    const h = halfMm(m.key, turn);
    let dx = 0, dz = 0, lift = 0;
    if (m.at && parent) {
      const g = M(m.at.gap), al = M(m.at.along ?? 0);
      if (m.at.side === 'front') { dx = parent.dx + al; dz = parent.dz + parent.hz + h.hz + g; }
      else if (m.at.side === 'back') { dx = parent.dx + al; dz = parent.dz - parent.hz - h.hz - g; }
      else if (m.at.side === 'right') { dx = parent.dx + parent.hx + h.hx + g; dz = parent.dz + al; }
      else { dx = parent.dx - parent.hx - h.hx - g; dz = parent.dz + al; }
      if (m.at.flush === 'back') dz = parent.dz - parent.hz + h.hz;
      else if (m.at.flush === 'front') dz = parent.dz + parent.hz - h.hz;
      else if (m.at.flush === 'left') dx = parent.dx - parent.hx + h.hx;
      else if (m.at.flush === 'right') dx = parent.dx + parent.hx - h.hx;
    } else if (m.on && parent) {
      dx = parent.dx + M(m.on.dx ?? 0); dz = parent.dz + M(m.on.dz ?? 0);
      lift = M(pieceMeta(parent.key)?.surface ?? pieceMeta(parent.key)?.drawn.h ?? 0);
    } else if (m.under && parent) {
      dx = parent.dx + M(m.under.dx ?? 0); dz = parent.dz + M(m.under.dz ?? 0);
    }
    const needs = [...new Set([...(anchorRole ? [anchorRole] : []), ...(m.needs ?? [])])];
    members.push({
      role: m.role, key: m.key, wants: m.wants, dx, dz, turn, hx: h.hx, hz: h.hz, tier: m.tier, optional: !!m.optional,
      blocksWalk: meta.blocksWalk && !m.on && !m.under, lift, on: m.on?.of, needs,
    });
  }
  const anchors: ResolvedAnchor[] = [];
  spec.anchors.forEach((a, index) => {
    const m = by(a.of);
    if (!m) return;
    const n = sideNormal(a.side);
    const dist = M(a.dist ?? 0.45), al = M(a.along ?? 0);
    const ax = m.dx + n.nx * (m.hx + dist) + (n.nx === 0 ? al : 0);
    const az = m.dz + n.nz * (m.hz + dist) + (n.nz === 0 ? al : 0);
    const e = a.envelope ?? { hx: 0.3, hz: 0.3 };
    // the envelope's hz runs along the side's normal
    const ehx = M(n.nx === 0 ? e.hx : e.hz), ehz = M(n.nx === 0 ? e.hz : e.hx);
    anchors.push({ spec: a, index, px: m.dx, pz: m.dz, ax, az, ehx, ehz });
  });
  let x0 = Infinity, z0 = Infinity, x1 = -Infinity, z1 = -Infinity;
  for (const m of members) {
    // (slice F) a procedural PIECE has a footprint whether or not it blocks a walk (a riser, a bay marking, a kneeler):
    // it counts in the kit's box, so a grid or a wall run never lays the next kit across it. A catalog underlay (a
    // rug) still does not — the wave-1 kits' boxes are unchanged.
    if (!m.blocksWalk && !(isKitProp(m.key) && !m.on)) continue;
    x0 = Math.min(x0, m.dx - m.hx); x1 = Math.max(x1, m.dx + m.hx); z0 = Math.min(z0, m.dz - m.hz); z1 = Math.max(z1, m.dz + m.hz);
  }
  if (!Number.isFinite(x0)) { x0 = z0 = x1 = z1 = 0; }
  const out: ResolvedKit = { spec, members, anchors, box: { x0, z0, x1, z1 } };
  kitCache.set(id, out);
  return out;
}

/** Every `wants` in the kit catalog: the WANTED ITEMS list, straight from the data. */
export function wantedItems(): Array<{ wants: string; standIn: string; kit: string; role: string }> {
  const out: Array<{ wants: string; standIn: string; kit: string; role: string }> = [];
  for (const k of ALL_KITS) for (const m of k.members) if (m.wants) out.push({ wants: m.wants, standIn: m.key, kit: k.id, role: m.role });
  return out;
}

/**
 * Where a fixture's MODEL stands, in the fixture's own frame (its origin is its base centre, +Z its front — the frame
 * `shellFixtures` seats it in): the catalog piece that draws it and the offset of that piece's base centre. The model's
 * BACK plane lies on the fixture's back plane (the wall it is mounted on) and its middle is at the fixture's middle in
 * height, so a model a little taller or shallower than the stand-in's box hangs where the box hung. A drawer turns
 * `dz` by the fixture's yaw: `x += sin(yaw)·dz`, `z += cos(yaw)·dz`, `y += dy`. Null when the fixture has no model.
 */
export function fixtureModelSeat(fixtureKey: string): { key: string; dy: number; dz: number } | null {
  const key = FIXTURE_MODEL[fixtureKey], fx = KIT_PROPS[fixtureKey];
  if (!key || !fx || !FURNITURE[key]) return null;
  const m = pieceMeta(key);
  if (!m) return null;
  return { key, dy: (fx.h - m.drawn.h) / 2, dz: -fx.d / 2 + m.drawn.d / 2 };
}

/** The kit versions at which a kit was RESEATED (it seats a key that joined at that version). */
export function kitReseatVersions(id: string): string[] {
  const k = KITS[id];
  if (!k) return [];
  const byKey = Object.entries(KIT_VERSION_KEYS).filter(([, keys]) => k.members.some((m) => keys.includes(m.key))).map(([v]) => v);
  const byKit = Object.entries(KIT_VERSION_KITS).filter(([, kits]) => kits.includes(id)).map(([v]) => v);
  return [...new Set([...byKey, ...byKit])].sort();
}

/** Is a piece key something a kit may seat: a catalog model or a procedural prop piece. */
export function isKitPiece(key: string): boolean {
  return !!FURNITURE[key] || KIT_PROPS[key]?.kind === 'piece';
}
